import React, { useRef, useState, useEffect } from "react";
import "./PainZoneSelector.css";
import body from '../../assets/body.jpg'
import { Button, Checkbox } from "antd";

const ZONE_COLORS = [
    "rgba(220, 38, 38, 0.85)",
    "rgba(37, 99, 235, 0.85)",
    "rgba(22, 163, 74, 0.85)",
    "rgba(234, 88, 12, 0.85)",
    "rgba(147, 51, 234, 0.85)",
];

const emptyZone = () => ({ point: null, arrow: null, radiates: false });

const PainZoneSelector = ({ onZonesChange }) => {
    const canvasRef = useRef(null);
    const [zones, setZones] = useState([emptyZone()]);
    const [activeIdx, setActiveIdx] = useState(0);

    const activeZone = zones[activeIdx] || emptyZone();
    const drawingArrow = !!(activeZone.point && activeZone.radiates && !activeZone.arrow);

    useEffect(() => {
        redraw();
    }, [zones]);

    useEffect(() => {
        if (onZonesChange) onZonesChange(zones);
    }, [zones]);

    function redraw() {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        zones.forEach((zone, i) => {
            const color = ZONE_COLORS[i % ZONE_COLORS.length];
            if (!zone.point) return;

            // Circle
            ctx.beginPath();
            ctx.arc(zone.point.x, zone.point.y, 12, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();
            ctx.strokeStyle = "white";
            ctx.lineWidth = 2;
            ctx.stroke();

            // Zone number label
            ctx.fillStyle = "white";
            ctx.font = "bold 13px Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(i + 1, zone.point.x, zone.point.y);

            // Arrow (irradia)
            if (zone.arrow) {
                const { start, end } = zone.arrow;
                ctx.beginPath();
                ctx.moveTo(start.x, start.y);
                ctx.lineTo(end.x, end.y);
                ctx.strokeStyle = color;
                ctx.lineWidth = 2;
                ctx.stroke();

                const angle = Math.atan2(end.y - start.y, end.x - start.x);
                ctx.beginPath();
                ctx.moveTo(end.x, end.y);
                ctx.lineTo(end.x - 12 * Math.cos(angle - Math.PI / 6), end.y - 12 * Math.sin(angle - Math.PI / 6));
                ctx.lineTo(end.x - 12 * Math.cos(angle + Math.PI / 6), end.y - 12 * Math.sin(angle + Math.PI / 6));
                ctx.closePath();
                ctx.fillStyle = color;
                ctx.fill();
            }
        });
    }

    const handleClick = (event) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        setZones(prev => prev.map((zone, i) => {
            if (i !== activeIdx) return zone;
            if (!zone.point) return { ...zone, point: { x, y } };
            if (drawingArrow) return { ...zone, arrow: { start: zone.point, end: { x, y } } };
            return zone;
        }));
    };

    const handleRadiatesChange = (e) => {
        const checked = e.target.checked;
        setZones(prev => prev.map((zone, i) => {
            if (i !== activeIdx) return zone;
            return { ...zone, radiates: checked, arrow: checked ? zone.arrow : null };
        }));
    };

    const handleReset = () => {
        setZones(prev => prev.map((zone, i) => i === activeIdx ? emptyZone() : zone));
    };

    const handleAddZone = () => {
        const newIdx = zones.length;
        setZones(prev => [...prev, emptyZone()]);
        setActiveIdx(newIdx);
    };

    const handleRemoveZone = (idx) => {
        setZones(prev => {
            const updated = prev.filter((_, i) => i !== idx);
            return updated.length > 0 ? updated : [emptyZone()];
        });
        setActiveIdx(prev => {
            if (prev === idx) return Math.max(0, idx - 1);
            if (prev > idx) return prev - 1;
            return prev;
        });
    };

    const getHint = () => {
        if (!activeZone.point) return `Haz clic en el cuerpo para marcar la Zona ${activeIdx + 1}`;
        if (drawingArrow) return 'Haz clic para indicar hacia dónde se irradia el dolor';
        return `Zona ${activeIdx + 1} marcada`;
    };

    return (
        <div className="pain-zone-selector">
            <h4>Seleccione sus zonas de dolor</h4>
            <div className="canvas-container">
                <img
                    src={body}
                    alt="Cuerpo humano"
                    className="background-image"
                    onLoad={redraw}
                />
                <canvas
                    style={{ borderRadius: 8, borderColor: '#6b809c', cursor: 'crosshair' }}
                    ref={canvasRef}
                    width={600}
                    height={625}
                    onClick={handleClick}
                />
            </div>

            <div style={{ marginTop: 12 }}>
                {zones.map((zone, i) => {
                    const color = ZONE_COLORS[i % ZONE_COLORS.length];
                    const isActive = activeIdx === i;
                    return (
                        <div key={i} style={{
                            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
                            marginBottom: 8, padding: '8px 12px', borderRadius: 6,
                            background: isActive ? '#f0f5ff' : '#fafafa',
                            border: `2px solid ${isActive ? color : '#e0e0e0'}`
                        }}>
                            <span style={{ fontWeight: 'bold', color, minWidth: 60 }}>Zona {i + 1}</span>
                            <span style={{ fontSize: 12, color: '#888' }}>
                                {zone.point ? '✓ Seleccionada' : 'Sin seleccionar'}
                            </span>
                            <Checkbox
                                checked={zone.radiates}
                                onChange={isActive ? handleRadiatesChange : undefined}
                                disabled={!isActive || !zone.point}
                            >
                                ¿Se irradia?
                            </Checkbox>
                            {!isActive && (
                                <Button size="small" onClick={() => setActiveIdx(i)}>Editar</Button>
                            )}
                            {isActive && (
                                <Button size="small" onClick={handleReset}>Borrar zona</Button>
                            )}
                            {zones.length > 1 && (
                                <Button size="small" danger onClick={() => handleRemoveZone(i)}>Eliminar</Button>
                            )}
                        </div>
                    );
                })}
                <p style={{ color: '#666', fontSize: 13, marginTop: 4 }}>{getHint()}</p>
            </div>

            <div className="button-container">
                <Button onClick={handleAddZone}>+ Agregar otra zona</Button>
            </div>
            <br />
        </div>
    );
};

export default PainZoneSelector;
